import { useEffect, useState } from 'react';
import HitlService, { HitlConfig, isSuperAdmin } from './HitlService';

/**
 * Global HITL config (spec watchlist-hitl-review.md §8.3, signal-pools §9.5). Singleton policy that
 * applies to ALL institutes. Visible to INTERNAL_ADMIN + SUPER_ADMIN (read); editable by SUPER_ADMIN
 * only (Save disabled otherwise — backend also enforces via superAdminAuth).
 */
const OUTCOMES = [
    { code: 'hiring', label: 'Hiring' }, { code: 'mentorship', label: 'Mentor' }, { code: 'donor', label: 'Donor' },
    { code: 'at', label: 'AT' }, { code: 'am', label: 'AM' }, { code: 'ra', label: 'RA' },
];
const STAGES = ['identified', 'validated', 'warmed', 'engaged', 'qualified', 'contribution', 'dormant', 'denied'];
const POOLS = [
    { code: 'cxo_leadership', label: 'CXO / Leadership' }, { code: 'entrepreneur', label: 'Claimed Entrepreneurs' },
    { code: 'premium_company', label: 'Premium Companies' }, { code: 'domain_experts', label: 'Domain Experts' },
    { code: 'active_alumni', label: 'Active Alumni' }, { code: 'dormant_alumni', label: 'Dormant Alumni' },
];

const key = (o: string, s: string) => `${o}__${s}`;

const HitlConfigPage = () => {
    const canEdit = isSuperAdmin();
    const [config, setConfig] = useState<HitlConfig | null>(null);
    const [gated, setGated] = useState<Set<string>>(new Set());
    const [poolCfg, setPoolCfg] = useState<HitlConfig['signal_pool_hitl_config']>({});
    const [hitlEnabled, setHitlEnabled] = useState(false);
    const [poolEnabled, setPoolEnabled] = useState(false);
    const [expiryDays, setExpiryDays] = useState<string>('');
    const [expiryAction, setExpiryAction] = useState<'reject' | 'approve' | 'none'>('reject');
    const [history, setHistory] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const hydrate = (c: HitlConfig) => {
        setConfig(c);
        setGated(new Set((c.hitl_required_transitions || []).filter((t) => t.layer === 'activation').map((t) => key(t.outcome, t.target_stage))));
        setPoolCfg(c.signal_pool_hitl_config || {});
        setHitlEnabled(c.hitl_enabled);
        setPoolEnabled(c.signal_pool_hitl_enabled);
        setExpiryDays(c.review_expiry_days == null ? '' : String(c.review_expiry_days));
        setExpiryAction(c.auto_action_on_expiry || 'reject');
    };

    useEffect(() => {
        HitlService.getConfig().then(hydrate).catch((e) => setError(e?.message || 'Failed to load config.'));
        HitlService.getConfigHistory().then((r) => setHistory(r.history || [])).catch(() => setHistory([]));
    }, []);

    const toggleCell = (o: string, s: string) => {
        if (!canEdit) return;
        setGated((g) => { const n = new Set(g); const k = key(o, s); n.has(k) ? n.delete(k) : n.add(k); return n; });
    };
    const togglePool = (pool: string, field: 'ambiguity_review_required' | 'removal_review_required') => {
        if (!canEdit) return;
        setPoolCfg((p) => ({ ...p, [pool]: { ...p[pool], [field]: !p[pool]?.[field] } }));
    };

    const save = async () => {
        setSaving(true);
        setMsg(null);
        setError(null);
        // Rebuild transitions, preserving existing reason/notes flags; new cells default reason_required=true.
        const existing = new Map((config?.hitl_required_transitions || []).map((t) => [key(t.outcome, t.target_stage), t]));
        const transitions = [...gated].map((k) => {
            const [outcome, target_stage] = k.split('__');
            const prev = existing.get(k);
            return { layer: 'activation', outcome, target_stage, reason_required: prev ? !!prev.reason_required : true, notes_required: prev ? !!prev.notes_required : false };
        });
        try {
            await HitlService.updateConfig({
                hitl_enabled: hitlEnabled,
                hitl_required_transitions: transitions as any,
                signal_pool_hitl_enabled: poolEnabled,
                signal_pool_hitl_config: poolCfg,
                review_expiry_days: expiryDays === '' ? null : Number(expiryDays),
                auto_action_on_expiry: expiryAction,
                change_reason: 'Edited via config UI',
            });
            setMsg('Saved.');
            const fresh = await HitlService.getConfig();
            hydrate(fresh);
            HitlService.getConfigHistory().then((r) => setHistory(r.history || []));
        } catch (e: any) {
            setError(e?.response?.data?.error?.message || e?.message || 'Save failed.');
        } finally {
            setSaving(false);
        }
    };

    if (error && !config) return <div className="main p-4"><div className="alert alert-danger">{error}</div></div>;
    if (!config) return <div className="main p-4 font-14 text-secondary">Loading config…</div>;

    return (
        <div className="main p-3 p-md-4">
            <div className="max-1140 mx-auto">
                <h2 className="m-0 font-22 font-600 text-dark-blue">HITL Configuration</h2>
                <p className="m-0 font-13 text-secondary-emphasis">Global policy — applies to all institutes.{!canEdit && ' Read-only (SUPER_ADMIN can edit).'}</p>
                {msg && <div className="alert alert-success font-12 mt-2">{msg}</div>}
                {error && <div className="alert alert-danger font-12 mt-2">{error}</div>}

                {/* Master switches + expiry */}
                <div className="bg-white border rounded-3 p-3 shadow-sm my-3 d-flex flex-wrap gap-4 align-items-center">
                    <label className="font-13"><input type="checkbox" className="me-2" checked={hitlEnabled} disabled={!canEdit} onChange={(e) => setHitlEnabled(e.target.checked)} />Stage HITL enabled</label>
                    <label className="font-13"><input type="checkbox" className="me-2" checked={poolEnabled} disabled={!canEdit} onChange={(e) => setPoolEnabled(e.target.checked)} />Pool HITL enabled</label>
                    <label className="font-13 d-flex align-items-center gap-2">Expiry (days)
                        <input type="number" className="form-control form-control-sm rounded-3" style={{ width: 90 }} value={expiryDays} disabled={!canEdit} onChange={(e) => setExpiryDays(e.target.value)} placeholder="never" />
                    </label>
                    <label className="font-13 d-flex align-items-center gap-2">On expiry
                        <select className="form-select form-select-sm rounded-3" style={{ width: 130 }} value={expiryAction} disabled={!canEdit} onChange={(e) => setExpiryAction(e.target.value as any)}>
                            <option value="reject">Auto-reject</option><option value="approve">Auto-approve</option><option value="none">Leave pending</option>
                        </select>
                    </label>
                </div>

                {/* Stage transition matrix */}
                <div className="bg-white border rounded-3 p-3 shadow-sm mb-3">
                    <div className="font-15 font-600 text-dark-blue mb-1">Stage Transition Gating</div>
                    <div className="font-12 text-secondary mb-3">Checked = HITL required when an alumni transitions INTO that stage for that outcome.</div>
                    <div className="table-responsive">
                        <table className="table table-sm align-middle text-center mb-0">
                            <thead className="font-11 text-uppercase text-muted">
                                <tr><th className="text-start">Outcome</th>{STAGES.map((s) => <th key={s} className="text-capitalize">{s}</th>)}</tr>
                            </thead>
                            <tbody>
                                {OUTCOMES.map((o) => (
                                    <tr key={o.code}>
                                        <td className="text-start font-13 font-600">{o.label}</td>
                                        {STAGES.map((s) => (
                                            <td key={s}><input type="checkbox" checked={gated.has(key(o.code, s))} disabled={!canEdit} onChange={() => toggleCell(o.code, s)} /></td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Pool HITL config */}
                <div className="bg-white border rounded-3 p-3 shadow-sm mb-3">
                    <div className="font-15 font-600 text-dark-blue mb-3">Pool HITL Configuration</div>
                    <table className="table table-sm align-middle mb-0">
                        <thead className="font-11 text-uppercase text-muted"><tr><th>Pool</th><th className="text-center">Review Ambiguity</th><th className="text-center">Review Removal</th></tr></thead>
                        <tbody>
                            {POOLS.map((p) => (
                                <tr key={p.code}>
                                    <td className="font-13 font-600">{p.label}</td>
                                    <td className="text-center"><input type="checkbox" checked={!!poolCfg[p.code]?.ambiguity_review_required} disabled={!canEdit} onChange={() => togglePool(p.code, 'ambiguity_review_required')} /></td>
                                    <td className="text-center"><input type="checkbox" checked={!!poolCfg[p.code]?.removal_review_required} disabled={!canEdit} onChange={() => togglePool(p.code, 'removal_review_required')} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="d-flex justify-content-end mb-4">
                    <button className="btn btn-primary px-4 rounded-3" disabled={!canEdit || saving} title={!canEdit ? 'SUPER_ADMIN only' : ''} onClick={save}>
                        {saving ? 'Saving…' : 'Save Configuration'}
                    </button>
                </div>

                {/* Config history */}
                <div className="bg-white border rounded-3 p-3 shadow-sm mb-4">
                    <div className="font-15 font-600 text-dark-blue mb-2">Change History</div>
                    <div className="table-responsive">
                        <table className="table table-sm mb-0 font-12">
                            <thead className="text-muted text-uppercase font-11"><tr><th>When</th><th>Stage HITL</th><th>Pool HITL</th><th>Expiry</th><th>On expiry</th><th>Reason</th></tr></thead>
                            <tbody>
                                {history.length === 0 ? <tr><td colSpan={6} className="text-muted">No history.</td></tr> :
                                    history.map((h) => (
                                        <tr key={h.id}>
                                            <td>{String(h.changed_at).replace('T', ' ').slice(0, 16)}</td>
                                            <td>{h.hitl_enabled ? 'on' : 'off'}</td>
                                            <td>{h.signal_pool_hitl_enabled ? 'on' : 'off'}</td>
                                            <td>{h.review_expiry_days ?? 'never'}</td>
                                            <td>{h.auto_action_on_expiry}</td>
                                            <td>{h.change_reason || '—'}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HitlConfigPage;
